import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type NumberInfoProps } from '../../core/store/map';
import { AppLogo } from '../../pages/icons';
import { MainLogo } from '../MainLogo';
import * as S from './styled';

interface NumberItem {
  name: string;
  text: string;
  highlighted?: boolean;
  blurred?: boolean;
}

interface NumberInfoModalProps {
  numberInfo: NumberInfoProps;
  withMainLogo?: boolean;
  withExtraInfo?: boolean;
  phoneNumberMarginTop?: number;
  listMarginBottom?: number;
  title?: string;
}

export const NumberInfoModal = ({
  numberInfo,
  withMainLogo = false,
  withExtraInfo = false,
  phoneNumberMarginTop = 16,
  listMarginBottom = 16,
  title,
}: NumberInfoModalProps) => {
  const { t } = useTranslation();
  const info: NumberItem[] = useMemo(
    () => [
      {
        name: t('carrier'),
        text: numberInfo.carrier,
      },
      {
        name: t('country'),
        text: numberInfo.country,
      },
      {
        name: t('timezone'),
        text: `(${numberInfo.timezone})`,
      },
      {
        name: t('city'),
        text: numberInfo.region,
        blurred: true,
      },
      {
        name: t('location'),
        text: t('defined'),
        highlighted: true,
      },
    ],
    [t, numberInfo],
  );

  const renderInfo = (item: NumberItem, index: number) => {
    const { name, text, highlighted = false, blurred = false } = item;
    if (!text) return null;

    return (
      <li key={index}>
        <S.InfoText>{name}</S.InfoText>
        <S.InfoText $isHighlighted={highlighted} $blurred={blurred}>
          {text}
        </S.InfoText>
      </li>
    );
  };

  return (
    <>
      {withMainLogo ? <MainLogo /> : AppLogo}
      <S.PhoneNumber $mt={phoneNumberMarginTop}>{numberInfo.phone_number}</S.PhoneNumber>
      <S.NumberText>{title ?? t('number_located')}</S.NumberText>
      <S.NumberInfoList $marginBottom={listMarginBottom}>{info.map(renderInfo)}</S.NumberInfoList>
      {withExtraInfo && (
        <>
          <S.ExtraText>{t('exact_gps')}</S.ExtraText>
          <S.ModalHint>{t('modal_hint')}</S.ModalHint>
        </>
      )}
    </>
  );
};
